import { type IconType } from '@assembly-js/design-system';
import { MenuItem } from '@mui/material';
import { FormikProps } from 'formik';
import { omit } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  APP_SETUP_PAGE,
  VisibilityRuleType,
  WEBHOOK_EVENTS,
} from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import history from 'src/history';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { DuplicateAppInstance } from 'src/legacy/components/AppSetup/DuplicateAppInstance';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { AppSettingsModalContext } from 'src/legacy/components/Modals/AppSettingsModalContext';
import ComboBox from 'src/legacy/components/Select/ComboBox';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { ApiKey } from 'src/legacy/components/UI/ApiKey';
import {
  useCreateAppMutation,
  useGetAppQuery,
  useGetInstallsQuery,
  useUpdateAppMutation,
} from 'src/services/api/applicationsApi';
import {
  clientRuleSchema,
  companyRuleSchema,
  customFieldRuleSchema,
  VisibilityScenario,
} from 'src/utils/app_visibility';

import { ModuleContainer } from '../../ModuleContainer';
import { SettingsPageForm } from '../../SettingsPageForm';
import SettingsToggleCard from '../../SettingsToggleCard';
import { useAppSetupPaths } from '../useAppSetupPaths';

import { SetupModulePageQueryParams } from './appSetupTypes';
import SetupAppBreadCrumbs from './SetupAppBreadCrumbs';
import { URLQueryParamsSection } from './URLQueryParamsSection';

import { SetupModulePageDescription } from '.';

const noVisibilityRuleSchema = z.object({
  ruleType: z.literal(VisibilityRuleType.NoVisibility),
});

const schema = z.object({
  name: z.string({ message: 'Name is required' }),
  // The design-system IconPicker returns FA/DS icon names outside the
  // legacy IconType enum. The picker will handle the real validation.
  icon: z
    .string()
    .min(1, 'Icon is required')
    // type-casting here to keep things strongly-typed in the form values and
    // avoid having to cast in multiple places
    .default('dashboard') as z.ZodType<IconType>,
  visibility: z.enum(['client', 'internal']),
  clientUrl: z.string().url('Client-facing URL is not a valid URL').optional(),
  internalUrl: z
    .string()
    .url('Internal-facing URL is not a valid URL')
    .optional(),
  notificationUrl: z
    .string()
    .url('Notification URL is not a valid URL')
    .optional(),
  enableWebhooks: z.boolean(),
  webhookUrl: z
    .string()
    .url('Webhook Endpoint URL is not a valid URL')
    .optional(),
  visibilityConfig: z.array(
    z.discriminatedUnion('ruleType', [
      clientRuleSchema,
      companyRuleSchema,
      customFieldRuleSchema,
      noVisibilityRuleSchema,
    ]),
  ),
  webhookEvents: z.array(z.enum(WEBHOOK_EVENTS)),
  queryParams: z
    .array(
      z.object({
        key: z.string().min(1, 'Key is required'),
        value: z.string().min(1, 'Value is required'),
      }),
    )
    .default([]),
});

type Schema = z.infer<typeof schema>;

const useStyles = makeStyles()(() => ({
  nameDescription: {
    display: 'inline-block',
  },

  section: {
    marginBottom: 40,
  },

  heading: {
    marginBottom: 16,
  },

  field: {
    marginBottom: 32,
  },
}));

const ensureWebhookEvents = (
  events: string[] | undefined,
): (typeof WEBHOOK_EVENTS)[number][] => {
  type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
  const eventObj: Partial<Record<WebhookEvent, true>> = {};
  WEBHOOK_EVENTS?.forEach((event) => (eventObj[event] = true));
  return (
    events?.filter<WebhookEvent>(
      (event): event is WebhookEvent => event in eventObj,
    ) ?? []
  );
};

function initializeEmptyVisibilityConfig(): Schema['visibilityConfig'] {
  return [];
}

const useInitialValues = (appId: string, moduleId: string) => {
  const { data: { moduleSettings, extensionsSettings } = {} } =
    useGetInstallsQuery();
  const module = moduleSettings?.find((m) => m.id === moduleId);
  const extension = module ? extensionsSettings?.[module.id] : undefined;
  const { data: app, isSuccess } = useGetAppQuery(
    { id: appId ?? '' },
    { skip: !appId, refetchOnMountOrArgChange: true },
  );
  return {
    isSuccess: !appId
      ? true
      : isSuccess && Boolean(extension) && Boolean(module),
    initialFormValue: {
      name: extension?.name ?? '',
      icon: extension?.icon ?? ('dashboard' as IconType),
      visibility: app?.clientUrl ? 'client' : 'internal',
      clientUrl: app?.clientUrl ?? '',
      internalUrl: app?.internalUrl ?? '',
      notificationUrl: app?.notificationUrl ?? '',
      webhookUrl: app?.webhookUrl ?? '',
      webhookEvents: ensureWebhookEvents(app?.webhookEvents ?? []),
      enableWebhooks: Boolean(app?.webhookUrl),
      visibilityConfig: initializeEmptyVisibilityConfig(),
      queryParams: extension?.queryParams ?? [],
    } as const,
  };
};

const useForm = () => {
  const { t } = useTranslation();

  const FormRenderer = ({
    values,
    errors,
    handleBlur,
    handleChange,
    touched,
    setFieldValue,
  }: FormikProps<Schema>) => {
    const { classes } = useStyles();
    const { query } = React.useContext(RouteContext);
    const { moduleId } = query as SetupModulePageQueryParams;
    const isAddingNewApp = moduleId === 'new';

    return (
      <>
        <section className={classes.section}>
          <BaseTypography fontType="18Medium" className={classes.heading}>
            Basic setup
          </BaseTypography>
          <BaseTextField
            className={classes.field}
            autoFocus
            fullWidth
            label={
              <>
                <BaseTypography
                  className={classes.nameDescription}
                  fontType="13Medium"
                >
                  Name
                </BaseTypography>
                <BaseTypography className={classes.nameDescription}>
                  &nbsp;- How it should appear in the sidebar
                </BaseTypography>
              </>
            }
            variant="outlined"
            sizeVariant="medium"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched?.name && errors?.name)}
            helperText={touched && touched.name && errors && errors.name}
          />

          <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
          <SuggestIconPicker
            value={values.icon}
            onChange={(iconName) => setFieldValue('icon', iconName)}
            appName={values.name}
          />
        </section>
        <section className={classes.section}>
          <BaseTypography fontType="18Medium" className={classes.heading}>
            Configure views
          </BaseTypography>
          <BaseTextField
            className={classes.field}
            sizeVariant="medium"
            select
            fullWidth
            variant="outlined"
            onChange={(e) => {
              if (e.target.value === 'internal') {
                setFieldValue('clientUrl', '');
              }
              handleChange(e);
            }}
            label="Setup type"
            onBlur={handleBlur}
            name="visibility"
            defaultValue="global"
            value={values.visibility}
          >
            <MenuItem value="client">
              {t(($) => $.page_apps.client_app_visibility_label)}
            </MenuItem>
            <MenuItem value="internal">
              {t(($) => $.page_apps.internal_app_visibility_label)}
            </MenuItem>
          </BaseTextField>

          {values.visibility === 'client' && (
            <BaseTextField
              className={classes.field}
              fullWidth
              label={
                <>
                  <BaseTypography
                    className={classes.nameDescription}
                    fontType="13Medium"
                  >
                    {t(($) => $.page_apps.client_facing_url.title)}
                  </BaseTypography>
                  <BaseTypography className={classes.nameDescription}>
                    {t(($) => $.page_apps.client_facing_url.description)}
                  </BaseTypography>
                </>
              }
              variant="outlined"
              sizeVariant="medium"
              name="clientUrl"
              value={values.clientUrl}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched?.clientUrl && errors?.clientUrl)}
              helperText={touched?.clientUrl && errors?.clientUrl}
            />
          )}

          <BaseTextField
            className={classes.field}
            fullWidth
            label={
              <>
                <BaseTypography
                  className={classes.nameDescription}
                  fontType="13Medium"
                >
                  {t(($) => $.page_apps.internal_facing_url.title)}
                </BaseTypography>
                <BaseTypography className={classes.nameDescription}>
                  {t(($) => $.page_apps.internal_facing_url.description)}
                </BaseTypography>
              </>
            }
            variant="outlined"
            sizeVariant="medium"
            name="internalUrl"
            value={values.internalUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched?.internalUrl && errors?.internalUrl)}
            helperText={touched?.internalUrl && errors?.internalUrl}
          />
          <BaseTextField
            className={classes.field}
            fullWidth
            label={
              <>
                <BaseTypography
                  className={classes.nameDescription}
                  fontType="13Medium"
                >
                  {t(($) => $.page_apps.notification_url.title)}
                </BaseTypography>
                <BaseTypography className={classes.nameDescription}>
                  {t(($) => $.page_apps.notification_url.description)}
                </BaseTypography>
              </>
            }
            variant="outlined"
            sizeVariant="medium"
            name="notificationUrl"
            value={values.notificationUrl}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched?.notificationUrl && errors?.notificationUrl)}
            helperText={touched?.notificationUrl && errors?.notificationUrl}
          />
        </section>
        <section className={classes.section}>
          <URLQueryParamsSection
            queryParams={values.queryParams}
            content={
              values.visibility === 'client'
                ? values.clientUrl
                : values.internalUrl
            }
            setFieldValue={setFieldValue}
          />
        </section>
        <section className={classes.section}>
          <BaseTypography fontType="18Medium" className={classes.heading}>
            Configure webhooks
          </BaseTypography>

          <div className={classes.field}>
            <SettingsToggleCard
              title="Enable webhooks"
              description="When enabled, you can specify a webhook endpoint URL and select specific webhooks to subscribe to"
              fieldName="enableWebhooks"
              checked={values.enableWebhooks}
            />
          </div>

          {values.enableWebhooks && (
            <>
              <BaseTextField
                className={classes.field}
                fullWidth
                label={
                  <>
                    <BaseTypography
                      className={classes.nameDescription}
                      fontType="13Medium"
                    >
                      Webhook Endpoint URL
                    </BaseTypography>
                  </>
                }
                variant="outlined"
                sizeVariant="medium"
                name="webhookUrl"
                value={values.webhookUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched?.webhookUrl && errors?.webhookUrl)}
                helperText={touched?.webhookUrl && errors?.webhookUrl}
                type="URL"
              />

              <ComboBox
                textFieldVariant="medium"
                error={Boolean(touched.webhookEvents && errors.webhookEvents)}
                helperText={touched.webhookEvents && errors.webhookEvents}
                id="events"
                label="Events to send"
                placeholder="Select events..."
                onBlur={handleBlur}
                onChange={(_, vals) => {
                  setFieldValue(
                    'webhookEvents',
                    vals.map((val) => val.id),
                  );
                }}
                values={values.webhookEvents.map((event) => ({
                  label: event,
                  id: event,
                  type: 'text',
                }))}
                options={WEBHOOK_EVENTS.map((eventName) => ({
                  label: eventName,
                  id: eventName,
                  type: 'text',
                }))}
                showPlaceholder
                multiple
              />
            </>
          )}
        </section>
        {values.visibility === 'client' && (
          <section>
            {isAddingNewApp ? (
              <AppVisibilityConfigSetting
                scenario={VisibilityScenario.AddAnApp}
                onVisibilityConfigSaved={(visibilityRules) => {
                  setFieldValue('visibilityConfig', visibilityRules);
                }}
              />
            ) : (
              <AppVisibilityConfigSetting
                scenario={VisibilityScenario.EditApp}
              />
            )}
          </section>
        )}
        {!isAddingNewApp && (
          <section className={classes.section}>
            <DuplicateAppInstance />
          </section>
        )}
        <section>
          <RemoveApp />
        </section>
      </>
    );
  };
  return {
    FormRenderer,
    validationScheme: toFormikValidationSchema(schema),
  };
};

export function SetupCustomApp() {
  const { portalHeader: portalId } = React.useContext(PortalConfigContext);
  const { query } = React.useContext(RouteContext);
  const modalCtx = React.useContext(AppSettingsModalContext);
  const { moduleId: moduleIdQueryParam, appId: appIdQueryParam } =
    query as SetupModulePageQueryParams;
  const { folderId, addAppPagePath } = useAppSetupPaths();
  const [createApp, createAppResult] = useCreateAppMutation();
  const [updateApp, updateAppResult] = useUpdateAppMutation();
  const hasEmbedUrl =
    createAppResult.isSuccess && Boolean(createAppResult.data?.internalUrl);
  const { isSuccess, initialFormValue } = useInitialValues(
    appIdQueryParam,
    moduleIdQueryParam,
  );
  // We wanted to enable creating apps with no URLs to facilitate
  // creating your API key before you deploy your app. That necessitates
  // fallback done states, sometimes we send folks to their app, sometimes
  // we send them to the app setup page.
  const handleDone = () => {
    if (modalCtx) {
      modalCtx.onClose();
    } else {
      history.push(APP_SETUP_PAGE.path);
    }
  };

  const handleCancel = () => {
    // cancel add custom app should redirect to add app page
    if (moduleIdQueryParam === 'new') {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(addAppPagePath);
      }
    }
  };

  React.useEffect(() => {
    if (updateAppResult.isSuccess) {
      if (modalCtx) {
        modalCtx.onClose();
      } else {
        history.push(APP_SETUP_PAGE.path);
      }
    }
  }, [updateAppResult, modalCtx]);

  if (!isSuccess) return null;

  return (
    <ModuleContainer description={<SetupModulePageDescription />}>
      <ModalWrapper
        noCancelButton
        width={480}
        onClose={handleDone}
        onSuccess={handleDone}
        open={createAppResult.isSuccess}
        successButtonLabel={hasEmbedUrl ? 'Go to App' : 'Done'}
      >
        {createAppResult.isSuccess && createAppResult.data?.apiKey && (
          <>
            <BaseTypography fontType="18Medium">
              Your app has been created
            </BaseTypography>
            <ApiKey apiKey={createAppResult.data.apiKey} />
          </>
        )}
      </ModalWrapper>
      <SettingsPageForm
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        initialFormValue={initialFormValue}
        useFormHook={useForm}
        validateOnMount={moduleIdQueryParam === 'new'}
        handleSave={async (values): Promise<boolean | void> => {
          const appValues = omit(values, 'icon', 'visibility', 'queryParams');
          if (moduleIdQueryParam === 'new') {
            const payload: Parameters<typeof createApp>[0] = {
              ...appValues,
              clientUrl: values.clientUrl ?? '',
              internalUrl: values.internalUrl ?? '',
              notificationUrl: values.notificationUrl ?? '',
              webhookUrl: values.webhookUrl ?? '',
              portalId,
              icon: values.icon,
              visibilityConfig: values.visibilityConfig,
              folderId,
              queryParams: values.queryParams,
            };
            await createApp(payload);
            // Prevent auto-close so the API key dialog can render
            if (modalCtx) return false;
          } else if (appIdQueryParam) {
            const payload: Parameters<typeof updateApp>[0] = {
              id: appIdQueryParam,
              ...appValues,
              clientUrl: values.clientUrl ?? '',
              internalUrl: values.internalUrl ?? '',
              notificationUrl: values.notificationUrl ?? '',
              webhookUrl: values.webhookUrl ?? '',
              portalId,
              moduleId: moduleIdQueryParam,
              icon: values.icon,
              queryParams: values.queryParams,
            };
            await updateApp(payload);
          }
        }}
        actionLabel={moduleIdQueryParam === 'new' ? 'Add' : 'Save'}
        handleCancel={handleCancel}
        showConfirmationModal={false}
        shouldShowPageAction={moduleIdQueryParam === 'new' ? true : undefined}
        enableDefaultErrorTooltip
      />
    </ModuleContainer>
  );
}
